// Station interface for sorting utility
export interface SortableStation {
  name: string
  _u_i_c_station_code: string
  sequence_number?: number
  short_code?: string
  country_code?: string
  district?: string
  county?: string
}

type Station = SortableStation

/**
 * Sorts stations alphabetically by name, with sequence number as secondary sort criteria.
 * When multiple stations have the same name match, prioritizes lower sequence numbers first.
 *
 * @param stations Array of stations to sort
 * @param searchQuery Optional search query to match against station names
 * @returns Sorted array of stations
 */
export const sortStationsAlphabetically = <T extends SortableStation>(
  stations: T[],
  searchQuery?: string,
): T[] => {
  let filteredStations = stations

  if (searchQuery && searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim()
    filteredStations = stations.filter((station) => station.name.toLowerCase().includes(query))
  }

  return filteredStations.sort((a, b) => {
    const nameA = a.name.toLowerCase()
    const nameB = b.name.toLowerCase()

    if (nameA < nameB) return -1
    if (nameA > nameB) return 1

    const seqA = a.sequence_number ?? 999
    const seqB = b.sequence_number ?? 999
    return seqA - seqB
  })
}

/**
 * Formats sorted stations for suggestion dropdown display
 *
 * @param stations Array of sorted stations
 * @returns Array of formatted suggestions with name, code, district, and county
 */
export const formatStationsForSuggestions = (stations: Station[]) => {
  return stations.map((station) => ({
    name: station.name,
    code: station._u_i_c_station_code,
    district: station.district,
    county: station.county,
  }))
}

/**
 * Combined utility: sorts stations and formats them for suggestions
 *
 * @param stations Array of stations to sort and format
 * @param searchQuery Search query to filter stations
 * @returns Formatted suggestions array
 */
export const getSortedStationSuggestions = (stations: Station[], searchQuery: string) => {
  if (!searchQuery.trim()) return []

  const query = searchQuery.toLowerCase().trim()
  const filteredStations = stations.filter((station) => station.name.toLowerCase().includes(query))

  const sortedStations = filteredStations.sort((a, b) => {
    const nameA = a.name.toLowerCase()
    const nameB = b.name.toLowerCase()
    const startsWithA = nameA.startsWith(query)
    const startsWithB = nameB.startsWith(query)

    if (startsWithA && !startsWithB) return -1
    if (!startsWithA && startsWithB) return 1

    if (startsWithA && startsWithB) {
      const seqA = a.sequence_number ?? 999
      const seqB = b.sequence_number ?? 999
      if (seqA !== seqB) return seqA - seqB
      return nameA.localeCompare(nameB, 'nb-NO', {
        sensitivity: 'base',
      })
    }

    return nameA.localeCompare(nameB, 'nb-NO', { sensitivity: 'base' })
  })

  return formatStationsForSuggestions(sortedStations)
}
